import AxiosInstance from '@/api/axios';
import { defaultPagination, PaginationInterface } from '@/components/Base/Pagination';
import { GeneralFilterType, UnansweredQuestionResponseType } from '@/utils/types';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

let currentAbortController: AbortController | null = null;

interface UnansweredFilterType extends GeneralFilterType {
  region_id?: string;
  page?: number;
  perpage?: number;
}

export const fetchUnansweredQuestion = createAsyncThunk(
  'getUnansweredQuestions/fetchData',
  async (
    { organization_id, branch_id, region_id, agent, date_range, page, perpage }: UnansweredFilterType,
    { rejectWithValue }
  ) => {
    if (!branch_id && !region_id) return null;
    if (branch_id === 'all') return null;

    if (currentAbortController) currentAbortController.abort();
    currentAbortController = new AbortController();
    const signal = currentAbortController.signal;

    try {
      const response = await AxiosInstance.get(`/queries`, {
        params: {
          organization_id,
          agents: agent,
          branch_id,
          date_range,
          handled: false,
          region_id,
          page,
          perpage,
        },
        signal,
      });

      if (response.data.success) return response.data.data;
      return null;
    } catch (error) {
      if ((error as any)?.name === 'CanceledError') return null;
      return rejectWithValue(error);
    } finally {
      currentAbortController = null;
    }
  }
);

interface IDeleteUnanswered {
  message_id: string;
  client: string;
  region?: string;
  branch?: string;
}

export const deleteUnansweredQuestion = createAsyncThunk(
  'unansweredQuestions/delete',
  async ({ message_id, client, region = '', branch = '' }: IDeleteUnanswered, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.delete(`/queries/${message_id}`, {
        params: { message_id, client, region, branch },
      });
      if (response.data.success) return message_id;
    } catch (error) {
      return rejectWithValue('An error occurred while deleting.');
    }
  }
);

interface UnansweredQuestionType {
  data: UnansweredQuestionResponseType | null | undefined;
  pagination: PaginationInterface;
  loading: boolean;
  error: string | null;
}

const initialState: UnansweredQuestionType = {
  data: null,
  pagination: defaultPagination,
  loading: false,
  error: null,
};

const unansweredQuestionReducer = createSlice({
  name: 'unansweredQuestions',
  initialState,
  reducers: {
    setPagination(state, action: PayloadAction<PaginationInterface>) {
      state.pagination = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUnansweredQuestion.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUnansweredQuestion.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.data = action.payload;
          state.pagination = {
            ...state.pagination,
            totalPages: action.payload.total_pages ?? state.pagination.totalPages,
            total: action.payload.total_count ?? state.pagination.total,
          };
        }
      })
      .addCase(fetchUnansweredQuestion.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteUnansweredQuestion.fulfilled, (state, action) => {
        const list = (state.data as any)?.queries_details;
        if (Array.isArray(list)) {
          (state.data as any).queries_details = list.filter(
            (q: any) => q._id !== action.payload
          );
          // Update total count after delete
          if (state.pagination.total !== null) {
            state.pagination.total -= 1;
            state.pagination.totalPages = Math.ceil(
              state.pagination.total / state.pagination.perpage
            );
          }
        }
      });
  },
});

export const { setPagination } = unansweredQuestionReducer.actions;
export default unansweredQuestionReducer.reducer;